import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Activity,
  ArrowRight,
  BookOpen,
  CalendarClock,
  ClipboardCheck,
  Clock,
  Gauge,
  Library,
  Loader2,
  PencilRuler,
  PlayCircle,
  Search,
  Target,
  Ticket,
  UserPlus,
  Users,
} from "lucide-react";
import {
  api,
  ApiError,
  AssessmentSummary,
  OrgDashboard,
  OrgUsage,
  SittingRow,
  TeacherDashboard,
} from "../api.js";
import { getActiveOrgSlug, getUser } from "../auth.js";
import { canManageSlips, hasRole } from "@/components/app/roles";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { LevelBadge } from "@/components/ui/level-badge";
import { Progress } from "@/components/ui/progress";
import { Skeleton } from "@/components/ui/skeleton";
import { toggleVariants } from "@/components/ui/toggle";
import { EmptyState } from "@/components/app/EmptyState";
import { ModuleFilterGroup } from "@/components/app/ModuleFilterGroup";
import { cn } from "@/lib/utils";
import { modulePalette } from "@/lib/modulePalette";
import { formatStampLocal } from "@/lib/questionMeta";
import { formatDateTime } from "@/lib/formatDateTime";
import { usePageTitle } from "@/lib/usePageTitle";
import { useUrlState } from "@/lib/useUrlState";
import { useProgramme } from "@/lib/useProgramme";
import { classifyWidgetError } from "@/lib/widgetLoadState";
import {
  compareScheduleActionable,
  parseScheduleStampMs,
  useScheduleTick,
  type ScheduleWindow,
} from "@/lib/scheduleTick";
import {
  MODULE_LABELS as CANONICAL_MODULE_LABELS,
  MODULE_ICONS as CANONICAL_MODULE_ICONS,
  modulesForProgramme,
  levelsForProgramme,
} from "@/lib/modules";

// Re-exports in the local namespace so the rest of this file reads
// unchanged. Using the canonical maps means HEDA modules pick up their
// friendly labels and icons instead of rendering as raw slugs.
const MODULE_LABELS = CANONICAL_MODULE_LABELS as Record<string, string>;
const MODULE_ICON = CANONICAL_MODULE_ICONS as unknown as Record<
  string,
  React.ComponentType<{ className?: string }>
>;

type FilterKey =
  | "all"
  | "reading"
  | "writing"
  | "speaking"
  | "listening"
  | "heda_english"
  | "heda_math"
  | "heda_ict";

type InProgressAttempt = {
  attempt_id: string;
  assessment_id: string;
  assessment_title: string;
  module: string;
  level_code: string;
  started_at: string;
  // Scheduling window of the underlying assessment, looked up from the
  // already-loaded catalogue (studentDashboard.recent_attempts doesn't carry
  // it). Lets the Resume card render the same closing-window pill the
  // catalogue grid + sittings strip use so a resuming candidate can see how
  // long the window stays open. Absent when the assessment isn't on a loaded
  // page (or has no window) — the card then renders no pill.
  opens_at?: string | null;
  closes_at?: string | null;
};

// Inside this much of the closing boundary the Resume card's pill flips to a
// destructive tone — a last-stretch cue for a candidate running out the clock.
const RESUME_CLOSING_URGENT_MS = 15 * 60_000;

export function Dashboard() {
  const nav = useNavigate();
  const [assessments, setAssessments] = useState<AssessmentSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [starting, setStarting] = useState<string | null>(null);
  const [resuming, setResuming] = useState<string | null>(null);
  const [inProgress, setInProgress] = useState<InProgressAttempt[]>([]);
  // Whether the in-progress / taken-assessments fetch has settled. Until
  // it has, we can't know if the candidate already has a blocking
  // attempt — so Start stays disabled to stop a fast click landing in a
  // raw server rejection between the grid rendering and this fetch
  // resolving.
  const [inProgressLoaded, setInProgressLoaded] = useState(false);
  // Distinct assessment ids the student has any non-cancelled attempt
  // on. Used to hide already-taken assessments from the available
  // grid so the student isn't offered a second sitting.
  const [takenAssessmentIds, setTakenAssessmentIds] = useState<Set<string>>(
    () => new Set(),
  );
  const [sittings, setSittings] = useState<SittingRow[] | null>(null);
  // Sittings strip + role-widget load/error tracking. `null` data already
  // doubles as "not loaded yet" for the strip, but the role widgets need an
  // explicit error flag so a 5xx can render a quiet inline Retry while a 403
  // stays hidden (classifyWidgetError makes that call).
  const [sittingsError, setSittingsError] = useState(false);
  const [moduleFilter, setModuleFilter] = useUrlState<FilterKey>(
    "module",
    "all",
  );
  // Title text-search + level chip — kept in URL state like moduleFilter so a
  // filtered catalogue is shareable. Both filter client-side over the rows
  // already loaded (the grid is a flat keyset list, so narrowing in-memory
  // avoids a refetch round-trip). The search debounces its URL write so we
  // don't push a history entry per keystroke.
  const [query, setQuery] = useUrlState<string>("q", "", 300);
  const [levelFilter, setLevelFilter] = useUrlState<string>("level", "all");
  // Keyset ("Load more") pagination — the list endpoint caps each page and
  // returns a next_cursor until the (filtered) list is exhausted. We thread it
  // into state and append on Load-more; loadingMore guards the append fetch so
  // a double-click can't duplicate a page. Earlier this grid dropped
  // next_cursor on the floor and silently showed only the first page.
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // In-progress attempts joined to the scheduling window of their underlying
  // assessment (matched by assessment_id against the already-loaded
  // catalogue). studentDashboard.recent_attempts doesn't carry the window, so
  // the Resume card looks it up here to render the closing-window pill. An
  // attempt whose assessment isn't on a loaded page keeps a null window and
  // simply renders no pill.
  const inProgressWithWindows = useMemo<InProgressAttempt[]>(() => {
    if (!assessments) return inProgress;
    const byId = new Map(assessments.map((a) => [a.id, a]));
    return inProgress.map((p) => {
      const a = byId.get(p.assessment_id);
      return a
        ? { ...p, opens_at: a.opens_at ?? null, closes_at: a.closes_at ?? null }
        : p;
    });
  }, [inProgress, assessments]);
  // Ticking wall-clock so the schedule pills + window-blocked Start/Open states
  // (and the sittings strip's actionable-first ordering) re-evaluate without a
  // manual reload. `Date.now()` was read once at render with no interval,
  // leaving an early candidate stuck on a frozen "Not yet open" until they
  // refreshed. useScheduleTick advances a coarse clock ONLY while some row has a
  // future open/close boundary (tightening to 5s within ~30s of one), and
  // cleans up / goes idle once everything has settled — no idle timer. The tick
  // watches the assessments grid, the sittings strip, and the resume cards'
  // windows so every pill stays live (and the resume card flips to its urgent
  // tone right on the closing stretch).
  const tickWindows = useMemo<ScheduleWindow[]>(
    () => [
      ...(assessments ?? []),
      ...(sittings ?? []),
      ...inProgressWithWindows,
    ],
    [assessments, sittings, inProgressWithWindows],
  );
  const nowTick = useScheduleTick(tickWindows);

  // Sittings ordered actionable-first: open/live (soonest-closing) →
  // not-yet-open (soonest-opening) → closed, recomputed on each tick so the
  // strip reorders the moment a sitting opens or closes. Pure data — we never
  // mutate or refetch; this only changes display order.
  const sortedSittings = useMemo(
    () =>
      sittings
        ? [...sittings].sort((a, b) =>
            compareScheduleActionable(a, b, nowTick),
          )
        : sittings,
    [sittings, nowTick],
  );

  const user = getUser();
  const isExaminer = useIsExaminer();
  const { active: activeProgramme } = useProgramme();
  const role = user?.role ?? "student";
  const isTenantAdmin = hasRole(role, "tenant_admin");
  const isTeacher = hasRole(role, "teacher");
  // Registrars (and anyone tenant_admin or above) manage Candidate Entry
  // Slips. `registrar` is non-hierarchical, so this can't be a hasRole rank
  // check — canManageSlips mirrors the API's requireSlipManager() allowlist.
  const isSlipManager = canManageSlips(role);
  const [orgDash, setOrgDash] = useState<OrgDashboard | null>(null);
  const [teacherDash, setTeacherDash] = useState<TeacherDashboard | null>(null);
  const [usage, setUsage] = useState<OrgUsage | null>(null);
  // Per-widget load flags so we can show a skeleton while the role widgets
  // resolve, and quiet inline errors (5xx) vs a silent hide (403). Admin
  // widgets share one flag pair (orgDashboard + getOrgUsage fetched together).
  const [adminWidgetsLoading, setAdminWidgetsLoading] = useState(isTenantAdmin);
  const [adminWidgetsError, setAdminWidgetsError] = useState(false);
  const [teacherWidgetLoading, setTeacherWidgetLoading] = useState(isTeacher);
  const [teacherWidgetError, setTeacherWidgetError] = useState(false);
  usePageTitle("Assessments");

  // Fetch the richer enterprise widgets lazily per role. A 403 is hidden
  // silently (classifyWidgetError → "hide") — a user without the role just
  // gets nothing extra rendered. A 5xx/network failure flips the error flag so
  // a quiet inline Retry renders instead. The widgets are fetched as a pair so
  // they share one skeleton + one retry.
  async function loadAdminWidgets() {
    setAdminWidgetsLoading(true);
    setAdminWidgetsError(false);
    try {
      const [dash, use] = await Promise.all([
        api.orgDashboard(),
        api.getOrgUsage(),
      ]);
      setOrgDash(dash);
      setUsage(use);
    } catch (e) {
      // 403 → not this user's widget; hide quietly. Anything else is a real
      // failure worth a Retry.
      if (classifyWidgetError(e) === "error") setAdminWidgetsError(true);
    } finally {
      setAdminWidgetsLoading(false);
    }
  }

  async function loadTeacherWidget() {
    setTeacherWidgetLoading(true);
    setTeacherWidgetError(false);
    try {
      setTeacherDash(await api.teacherDashboard());
    } catch (e) {
      if (classifyWidgetError(e) === "error") setTeacherWidgetError(true);
    } finally {
      setTeacherWidgetLoading(false);
    }
  }

  useEffect(() => {
    if (!isTenantAdmin) return;
    void loadAdminWidgets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTenantAdmin]);

  useEffect(() => {
    if (!isTeacher) return;
    void loadTeacherWidget();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTeacher]);

  // Published sittings surface in a strip above the assessments
  // grid so a candidate whose slip isn't pinned to one can still
  // find and open any that they're entitled to. A 403 (older tenant /
  // not entitled) just hides the strip; a 5xx renders a quiet inline
  // Retry so the candidate isn't left wondering where their sitting went.
  async function loadSittings() {
    setSittings(null);
    setSittingsError(false);
    try {
      const res = await api.listSittings({
        status: "published",
        // Scope to the active programme so a HEDA student doesn't see
        // FS-English sittings in the strip (and vice versa). The effect below
        // re-runs this on a programme switch.
        ...(activeProgramme?.slug ? { programme: activeProgramme.slug } : {}),
      });
      setSittings(res.sittings);
    } catch (e) {
      if (classifyWidgetError(e) === "error") {
        setSittingsError(true);
      } else {
        // 403 / not-entitled — treat as "no sittings", strip stays hidden.
        setSittings([]);
      }
    }
  }

  useEffect(() => {
    void loadSittings();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeProgramme?.slug]);

  useEffect(() => {
    // Reset the module filter when the active programme changes so we don't
    // end up with a stale module selection that doesn't exist in the new
    // programme's module set.
    if (
      moduleFilter !== "all" &&
      activeProgramme &&
      !modulesForProgramme(activeProgramme.slug).includes(moduleFilter as never)
    ) {
      setModuleFilter("all");
      return;
    }
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [moduleFilter, activeProgramme?.slug]);

  // The level chip is a client-side filter (no refetch) — but a stale L2
  // selection makes no sense once the active programme is HEDA (D1–D3), so
  // drop it back to "all" when the programme changes to a set that excludes it.
  // Kept separate from load()'s effect so changing the level never triggers a
  // refetch that would discard pages already pulled via "Load more".
  useEffect(() => {
    if (
      levelFilter !== "all" &&
      activeProgramme &&
      !levelsForProgramme(activeProgramme.slug).includes(levelFilter)
    ) {
      setLevelFilter("all");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [levelFilter, activeProgramme?.slug]);

  // In-progress attempts come from the student-only dashboard endpoint. If the
  // signed-in user isn't a student (e.g. admin), the 403 is swallowed — the
  // resume strip simply doesn't render. Extracted so start() can re-run it to
  // re-sync the blocking/taken grid after a rejected start (a stale grid would
  // otherwise keep offering Start on an assessment the server just blocked).
  async function loadStudentDashboard() {
    if (user?.role !== "student") {
      // Non-students never have a blocking attempt and don't see Start, so
      // treat their in-progress state as "settled" immediately.
      setInProgressLoaded(true);
      return;
    }
    setInProgressLoaded(false);
    try {
      const d = await api.studentDashboard();
      setInProgress(
        d.recent_attempts
          .filter((a) => a.status === "in_progress")
          .map((a) => ({
            attempt_id: a.attempt_id,
            assessment_id: a.assessment_id,
            assessment_title: a.assessment_title,
            module: a.module,
            level_code: a.level_code,
            started_at: a.started_at,
          })),
      );
      setTakenAssessmentIds(new Set(d.taken_assessment_ids ?? []));
    } catch {
      // Not a student, or API down — skip the strip silently.
    } finally {
      // Settled (success or failure): Start may now reflect the real
      // blocking state.
      setInProgressLoaded(true);
    }
  }

  useEffect(() => {
    void loadStudentDashboard();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.role]);

  async function resume(attemptId: string) {
    setResuming(attemptId);
    setError(null);
    try {
      // Happy path — the bootstrap is still in sessionStorage from this tab.
      // A sitting-attached attempt is annotated with sitting_id in the
      // stored blob by the SittingHub's enter() call, so we can route
      // back to the hub without an extra fetch.
      const existing = sessionStorage.getItem(`attempt:${attemptId}`);
      if (existing) {
        let boot: { sitting_id?: string | null } = {};
        try {
          boot = JSON.parse(existing);
        } catch {
          /* malformed — fall through to the no-bootstrap path */
        }
        if (boot.sitting_id) {
          nav(`/sittings/${boot.sitting_id}/overview`);
          return;
        }
        nav(`/attempts/${attemptId}/run`);
        return;
      }
      // Fresh tab / new device. Check the attempt's server-side state before
      // deciding how to handle it.
      const a = await api.getAttempt(attemptId);
      if (
        a.status === "submitted" ||
        a.status === "auto_marked" ||
        a.status === "awaiting_review" ||
        a.status === "awaiting_moderation" ||
        a.status === "reviewed"
      ) {
        nav(`/attempts/${attemptId}/result`);
        return;
      }
      if (a.status !== "in_progress") {
        setError(
          "This attempt is no longer available. Please start the assessment again.",
        );
        return;
      }
      // Sitting-attached in-progress: send them through the hub so
      // the slot's pause-resume state gets re-synced (elapsed /
      // last_active_at / fresh expires_at).
      if (a.sitting_id) {
        nav(`/sittings/${a.sitting_id}/overview`);
        return;
      }
      // Standalone in-progress with no bootstrap — redirect to TestRunner
      // with a resume hint so it can offer a graceful "restart" path
      // instead of a crash.
      nav(`/attempts/${attemptId}/run`, { state: { resume: true } });
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "failed to resume");
    } finally {
      setResuming(null);
    }
  }

  // Shared query params for the active server-side filters, so the first page
  // and every "Load more" page filter identically. Title/level are applied
  // client-side over the loaded rows, so they're deliberately NOT here.
  function listParams() {
    return {
      ...(moduleFilter !== "all" ? { module: moduleFilter } : {}),
      ...(activeProgramme?.slug ? { programme: activeProgramme.slug } : {}),
      status: "published" as const,
    };
  }

  async function load() {
    setAssessments(null);
    setError(null);
    setNextCursor(null);
    try {
      const res = await api.listAssessments(listParams());
      setAssessments(res.assessments);
      // Older deployments / cached responses may omit next_cursor — coerce a
      // missing value to null so we treat the page as exhausted rather than
      // rendering a Load-more button that fetches nothing.
      setNextCursor(res.next_cursor ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "failed to load");
    }
  }

  // Fetch the next keyset page and APPEND it to the current list.
  async function loadMore() {
    if (!nextCursor || loadingMore) return;
    setLoadingMore(true);
    setError(null);
    try {
      const res = await api.listAssessments({
        ...listParams(),
        cursor: nextCursor,
      });
      setAssessments((prev) => [...(prev ?? []), ...res.assessments]);
      setNextCursor(res.next_cursor ?? null);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "failed to load more");
    } finally {
      setLoadingMore(false);
    }
  }

  async function start(id: string) {
    setStarting(id);
    setError(null);
    try {
      const res = await api.startAttempt(id);
      sessionStorage.setItem(`attempt:${res.attempt_id}`, JSON.stringify(res));
      nav(`/attempts/${res.attempt_id}/run`);
    } catch (e) {
      setError(e instanceof ApiError ? e.message : "failed to start attempt");
      // The server rejected the start — commonly because a blocking attempt
      // already exists or the assessment was just taken on another device.
      // Our local grid can be stale (still showing an enabled Start), so
      // re-fetch the student dashboard to re-sync inProgress / takenAssessmentIds
      // and bring the grid back in line with the server's truth.
      void loadStudentDashboard();
    } finally {
      setStarting(null);
    }
  }

  // Any in-progress attempt blocks every remaining Start button. The
  // API enforces the same rule server-side.
  const hasBlockingAttempt = inProgress.length > 0;
  // A start is in flight on SOME card. Disable every Start until it settles so
  // a fast user can't fire concurrent starts across cards before the first
  // navigation lands (each start creates an attempt; a second would race the
  // blocking-attempt guard and land in a raw server rejection).
  const anyStarting = starting != null;
  // Hide every assessment the candidate has any non-cancelled
  // attempt on — submitted, scored, awaiting review, or reviewed.
  // Once a student has sat an assessment the platform treats it as
  // complete for them (the combined transcript is the record of the
  // outcome). `takenAssessmentIds` is a superset of the in-progress
  // ones so we don't also subtract those separately.
  const hiddenAssessmentIds = takenAssessmentIds;
  // The assessments actually shown in the grid after hiding already-taken
  // and in-progress papers. Extracted so the filter-status live region can
  // announce the same count the candidate sees (and the grid maps over the
  // identical list rather than re-deriving it).
  // Title/level filters narrow the LOADED rows in-memory. Trim + lowercase the
  // query once so the per-card check is a cheap substring match.
  const trimmedQuery = query.trim().toLowerCase();
  const visibleAssessments = (assessments ?? []).filter((a) => {
    if (hiddenAssessmentIds.has(a.id)) return false;
    if (inProgress.some((p) => p.assessment_id === a.id)) return false;
    if (levelFilter !== "all" && a.level_code !== levelFilter) return false;
    if (trimmedQuery && !a.title.toLowerCase().includes(trimmedQuery))
      return false;
    return true;
  });
  // Whether a title/level filter is currently narrowing the loaded list. Used
  // to tailor the empty-state copy (and offer a "clear" affordance) when the
  // client-side filter — not an empty server page — is what hid every card.
  const clientFiltered = trimmedQuery !== "" || levelFilter !== "all";
  const levelOptions = levelsForProgramme(activeProgramme?.slug ?? "fs_english");
  // The available grid (and its Start CTAs) must wait for BOTH the assessments
  // list AND — for students — the student-dashboard fetch that supplies
  // takenAssessmentIds / inProgress. Without gating on inProgressLoaded a
  // student briefly sees a Start card for an assessment they've already taken,
  // which then vanishes once the dashboard fetch settles. Keep the skeleton up
  // through that window so the grid only paints once it's correct.
  const gridLoading =
    !assessments || (user?.role === "student" && !inProgressLoaded);

  return (
    <div className="space-y-6">
      <IdentityHero
        user={user}
        orgSlug={getActiveOrgSlug()}
        assessmentsAvailable={
          // Students see the count that matches the cards below — the
          // visible set with already-taken / in-progress papers removed.
          // Staff don't have that hiding applied, so they keep the raw total.
          assessments == null
            ? null
            : user?.role === "student"
              ? visibleAssessments.length
              : assessments.length
        }
        inProgressCount={inProgress.length}
        isExaminer={isExaminer}
        programmeName={activeProgramme?.name ?? null}
        programmeSlug={activeProgramme?.slug ?? null}
      />

      {/* Candidate guide pointer. A first-time candidate lands here with no idea
          what to expect; point them at the walkthrough + the results guide. The
          same articles live in the Help Centre (sidebar), this is just an
          in-flow shortcut. Students only. */}
      {user?.role === "student" && (
        <div className="flex flex-col gap-3 rounded-lg border bg-card p-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <BookOpen className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden />
            <div className="space-y-0.5">
              <p className="text-sm font-medium">New to sitting exams here?</p>
              <p className="text-sm text-muted-foreground">
                Read the short guides on sitting your exam and on how results,
                grades and bands work.
              </p>
            </div>
          </div>
          <div className="flex shrink-0 flex-wrap gap-2">
            <Button asChild size="sm" variant="outline">
              <Link to="/help/sitting-your-exam">
                Sitting your exam
                <ArrowRight className="ml-1 h-3.5 w-3.5" aria-hidden />
              </Link>
            </Button>
            <Button asChild size="sm" variant="ghost">
              <Link to="/help/understanding-your-result">Your result</Link>
            </Button>
          </div>
        </div>
      )}

      {/* First-run checklist for tenant admins whose org is empty. Hidden
          for students + for non-empty orgs so it doesn't clutter the
          everyday view. */}
      {user?.role === "tenant_admin" && (assessments?.length ?? 0) === 0 && (
        <FirstRunChecklist />
      )}

      {/* Role-aware quick actions strip. Power users use this more than the
          sidebar because it's in-flow with the header. */}
      {isTeacher && (
        <QuickActions
          isExaminer={isExaminer}
          isTenantAdmin={isTenantAdmin}
          isSlipManager={isSlipManager}
        />
      )}

      {/* Prominent Entry-slips CTA for slip managers (registrars + admins).
          Registrars otherwise land on a grid of assessments they can't act
          on — this points them straight at their actual job. Sits above the
          grid so it's the first thing they reach for. */}
      {isSlipManager && <SlipManagerCTA />}

      {/* Tenant admin KPIs + plan usage + recent audit. A skeleton holds the
          space while the org dash fetch is in flight (so the metrics don't
          pop in late and shove the page down); a 5xx renders a quiet inline
          Retry while a 403 leaves the area blank. We never flash zeroes —
          the real tiles only render once orgDash resolves. */}
      {isTenantAdmin && adminWidgetsLoading && <WidgetSkeleton rows={4} />}
      {isTenantAdmin && !adminWidgetsLoading && adminWidgetsError && (
        <WidgetError onRetry={() => void loadAdminWidgets()} />
      )}
      {isTenantAdmin && orgDash && (
        <OrgMetrics headline={orgDash.headline} />
      )}

      {isTenantAdmin && usage && <PlanUsageWidget usage={usage} />}

      {/* Teacher insights — highlights awaiting-review count (an examiner's
          attention grabber) and the top assessments by volume. */}
      {isTeacher && teacherWidgetLoading && <WidgetSkeleton rows={2} />}
      {isTeacher && !teacherWidgetLoading && teacherWidgetError && (
        <WidgetError onRetry={() => void loadTeacherWidget()} />
      )}
      {isTeacher && teacherDash && (
        <TeacherInsights
          totals={teacherDash.totals}
          byAssessment={teacherDash.by_assessment.slice(0, 5)}
          isExaminer={isExaminer}
        />
      )}

      {isTenantAdmin && orgDash && orgDash.recent_audit.length > 0 && (
        <RecentAuditFeed events={orgDash.recent_audit.slice(0, 8)} />
      )}


      {inProgress.length > 0 && (
        <section className="space-y-2">
          <div
            role="status"
            aria-live="polite"
            className="flex flex-wrap items-start gap-2 rounded-md border border-warning/50 bg-warning/10 px-3 py-2 text-sm text-foreground"
          >
            <PlayCircle
              className="mt-0.5 h-4 w-4 shrink-0 text-warning-text"
              aria-hidden
            />
            <span>
              <strong className="font-semibold text-warning-text">
                You have{" "}
                {inProgress.length === 1 ? "an exam" : `${inProgress.length} exams`}{" "}
                in progress.
              </strong>{" "}
              Finish or submit {inProgress.length === 1 ? "it" : "them"} before
              starting a new assessment. The rest of the list is disabled
              until you do.
            </span>
          </div>
          <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            In progress
          </h2>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {inProgressWithWindows.map((a) => {
              // Same closing-window pill the catalogue grid + sittings strip
              // use, sourced from the assessment window joined above and fed
              // the ticking clock so it counts down live. Pass the urgent
              // threshold so the "Closes …" pill flips to a destructive tone
              // as the candidate runs out the closing stretch. No window → no
              // pill.
              const opensMs = parseScheduleStampMs(a.opens_at);
              const closesMs = parseScheduleStampMs(a.closes_at);
              const pill = dashboardSchedulePill(
                opensMs,
                closesMs,
                nowTick,
                RESUME_CLOSING_URGENT_MS,
              );
              return (
              <Card key={a.attempt_id} className="border-warning/40 bg-warning/5">
                <CardHeader className="space-y-2 pb-3">
                  <div className="flex flex-wrap gap-1.5">
                    <LevelBadge code={a.level_code} />
                    <Badge variant="secondary">{MODULE_LABELS[a.module] ?? a.module}</Badge>
                    <Badge variant="warning">in progress</Badge>
                  </div>
                  <CardTitle className="text-base leading-snug">{a.assessment_title}</CardTitle>
                </CardHeader>
                <CardContent className="space-y-2 pb-2 text-xs text-muted-foreground">
                  {pill && (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                        pill.classes,
                      )}
                    >
                      <CalendarClock className="h-3 w-3" aria-hidden />
                      {pill.label}
                    </span>
                  )}
                  <div>Started {formatDateTime(a.started_at)}</div>
                </CardContent>
                <CardFooter>
                  <Button
                    size="sm"
                    onClick={() => void resume(a.attempt_id)}
                    loading={resuming === a.attempt_id}
                    className="w-full"
                  >
                    {resuming === a.attempt_id ? null : (
                      <PlayCircle className="mr-2 h-4 w-4" />
                    )}
                    Resume
                  </Button>
                </CardFooter>
              </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Sittings strip skeleton — `sittings === null` means the fetch is
          still in flight. Holds the space so the strip doesn't pop in late
          and push the assessments grid down. A 403/empty result resolves to
          [] and renders nothing; a 5xx flips sittingsError for a quiet Retry. */}
      {sittings === null && !sittingsError && (
        <section className="space-y-3">
          <Skeleton className="h-4 w-24" />
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {[0, 1, 2].map((i) => (
              <Card key={i}>
                <CardHeader className="space-y-3">
                  <div className="flex gap-2">
                    <Skeleton className="h-5 w-12" />
                    <Skeleton className="h-5 w-16" />
                  </div>
                  <Skeleton className="h-5 w-3/4" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-4 w-2/3" />
                </CardContent>
                <CardFooter>
                  <Skeleton className="h-9 w-full" />
                </CardFooter>
              </Card>
            ))}
          </div>
        </section>
      )}

      {sittingsError && (
        <WidgetError
          label="Couldn't load your sittings."
          onRetry={() => void loadSittings()}
        />
      )}

      {sittings && sittings.length > 0 && (
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
            Sittings
          </h2>
          <p className="text-xs text-muted-foreground">
            {sittings.some((s) => s.order_mode === "linear")
              ? "A sitting groups multiple tests into one session. Some run in a fixed order. Follow the sequence shown; each test keeps its own timer."
              : "A sitting groups multiple tests into one session. Enter any test, save and switch between them, each keeps its own timer."}
          </p>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {(sortedSittings ?? []).map((s) => {
              // Parse the window once, then feed the SAME boundaries to both
              // the pill and the disabled-state derivation so a "Closed"/"Not
              // yet open" sitting renders a disabled button instead of an
              // enabled "Open sitting" Link the server would reject.
              const opensMs = parseScheduleStampMs(s.opens_at);
              const closesMs = parseScheduleStampMs(s.closes_at);
              const pill = dashboardSchedulePill(opensMs, closesMs, nowTick);
              const notYetOpen = opensMs != null && nowTick < opensMs;
              const alreadyClosed = closesMs != null && nowTick >= closesMs;
              const windowBlocked = notYetOpen || alreadyClosed;
              return (
              <Card key={s.id} className="flex flex-col overflow-hidden">
                {/* Module icon strip — same pattern as SittingsList.
                    Pre-cues the colour palette the student will see
                    once they enter the SittingHub, so the first
                    touch matches the second. */}
                {s.modules && s.modules.length > 0 && (
                  <div className="flex items-center gap-1.5 border-b bg-muted/30 px-4 py-2">
                    {s.modules.map((m, i) => {
                      const palette = modulePalette(m);
                      const Icon = palette.icon;
                      return (
                        <span
                          key={`${m}-${i}`}
                          className={cn(
                            "inline-flex h-6 w-6 items-center justify-center rounded-md",
                            palette.accentBg,
                            palette.accentText,
                          )}
                          title={(MODULE_LABELS as Record<string, string>)[m] ?? m}
                          aria-label={(MODULE_LABELS as Record<string, string>)[m] ?? m}
                        >
                          <Icon className="h-3.5 w-3.5" aria-hidden />
                        </span>
                      );
                    })}
                    <span className="ml-auto text-[10px] text-muted-foreground">
                      {s.modules.length} test{s.modules.length === 1 ? "" : "s"}
                    </span>
                  </div>
                )}
                <CardHeader className="space-y-2 pb-3">
                  <div className="flex flex-wrap gap-1.5">
                    <LevelBadge code={s.level_code} />
                    <Badge variant="secondary">Sitting</Badge>
                    {s.order_mode === "linear" && (
                      <Badge variant="outline">In order</Badge>
                    )}
                  </div>
                  <CardTitle className="text-base leading-snug">
                    {s.title}
                  </CardTitle>
                </CardHeader>
                <CardContent className="mt-auto space-y-2 text-xs text-muted-foreground">
                  {pill && (
                    <span
                      className={cn(
                        "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                        pill.classes,
                      )}
                    >
                      <CalendarClock className="h-3 w-3" aria-hidden />
                      {pill.label}
                    </span>
                  )}
                  {s.opens_at && (
                    <div title={formatDateTime(s.opens_at)}>
                      Opens: {formatStampLocal(s.opens_at)}
                    </div>
                  )}
                  {s.closes_at && (
                    <div title={formatDateTime(s.closes_at)}>
                      Closes: {formatStampLocal(s.closes_at)}
                    </div>
                  )}
                  {!s.opens_at && !s.closes_at && (
                    <div>Available anytime while published.</div>
                  )}
                </CardContent>
                <CardFooter>
                  {windowBlocked ? (
                    <Button
                      type="button"
                      className="w-full"
                      disabled
                      title={
                        notYetOpen
                          ? "This sitting hasn't opened yet"
                          : "This sitting has closed"
                      }
                    >
                      <CalendarClock className="mr-2 h-4 w-4" />
                      {notYetOpen ? "Not yet open" : "Closed"}
                    </Button>
                  ) : (
                    <Button asChild className="w-full">
                      <Link to={`/sittings/${s.id}/overview`}>
                        <PlayCircle className="mr-2 h-4 w-4" /> Open sitting
                      </Link>
                    </Button>
                  )}
                </CardFooter>
              </Card>
              );
            })}
          </div>
        </section>
      )}

      {/* Filter bar — module radio group + level chip + title search. Module
          is a server-side filter (refetches); level + title narrow the loaded
          rows client-side. All three are mirrored into the URL so a narrowed
          catalogue is shareable. */}
      <div className="flex flex-col gap-3">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <ModuleFilterGroup
            modules={["all", ...modulesForProgramme(activeProgramme?.slug ?? "fs_english")]}
            value={moduleFilter}
            onChange={(m) => setModuleFilter(m as typeof moduleFilter)}
          />
          <div className="relative w-full sm:w-64">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              type="search"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search by title"
              aria-label="Search assessments by title"
              className="pl-8"
            />
          </div>
        </div>
        <LevelFilterGroup
          levels={["all", ...levelOptions]}
          value={levelFilter}
          onChange={setLevelFilter}
        />
      </div>

      {/* Polite, visually-hidden count so screen-reader users hear how the
          filter changed the list (sighted users see the grid update). Only
          announced once the assessments fetch has resolved. */}
      {!gridLoading && assessments && (
        <p role="status" aria-live="polite" className="sr-only">
          {moduleFilter === "all"
            ? `Showing ${visibleAssessments.length} assessment${
                visibleAssessments.length === 1 ? "" : "s"
              }.`
            : `Showing ${visibleAssessments.length} ${
                MODULE_LABELS[moduleFilter] ?? moduleFilter
              } assessment${visibleAssessments.length === 1 ? "" : "s"}.`}
        </p>
      )}

      {error && (
        <div
          role="alert"
          className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
        >
          <span>{error}</span>
          <Button
            variant="ghost"
            size="sm"
            className="h-7 shrink-0 text-destructive hover:text-destructive"
            onClick={() => void load()}
          >
            Retry
          </Button>
        </div>
      )}

      {gridLoading && !error && (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {[0, 1, 2].map((i) => (
            <Card key={i}>
              <CardHeader className="space-y-3">
                <div className="flex gap-2">
                  <Skeleton className="h-5 w-12" />
                  <Skeleton className="h-5 w-20" />
                </div>
                <Skeleton className="h-5 w-3/4" />
              </CardHeader>
              <CardContent>
                <Skeleton className="h-4 w-full" />
              </CardContent>
              <CardFooter>
                <Skeleton className="h-9 w-32" />
              </CardFooter>
            </Card>
          ))}
        </div>
      )}

      {!gridLoading && assessments && assessments.length === 0 && (
        <EmptyState
          icon={BookOpen}
          title={
            moduleFilter !== "all"
              ? `No ${MODULE_LABELS[moduleFilter]} assessments`
              : "No published assessments"
          }
          description={
            moduleFilter !== "all"
              ? "Try clearing the filter or pick another module."
              : isTeacher
                ? // Staff own the catalogue — point them at authoring rather
                  // than the student "your teachers haven't published" framing.
                  "Nothing is published yet. Create an assessment to get your cohort started."
                : "Your teachers haven't published anything yet. Check back soon."
          }
          cta={
            moduleFilter !== "all"
              ? { label: "Show all", onClick: () => setModuleFilter("all") }
              : isTeacher
                ? { label: "Create an assessment", to: "/authoring" }
                : undefined
          }
        />
      )}

      {/* The server returned rows but a client-side title/level filter hid them
          all — distinct from "the server has nothing". Offer a one-click reset
          so the grid never strands the user on a silently-empty view. */}
      {!gridLoading &&
        assessments &&
        assessments.length > 0 &&
        visibleAssessments.length === 0 &&
        clientFiltered && (
          <EmptyState
            icon={Search}
            title="No assessments match your search"
            description={
              nextCursor
                ? "Nothing on the loaded pages matches. Try Load more, or clear the title/level filter."
                : "Try a different title or level, or clear the filter to see everything."
            }
            cta={{
              label: "Clear filters",
              onClick: () => {
                setQuery("");
                setLevelFilter("all");
              },
            }}
          />
        )}

      {!gridLoading &&
        assessments &&
        assessments.length > 0 &&
        assessments.every((a) => hiddenAssessmentIds.has(a.id)) &&
        // hiddenAssessmentIds (taken_assessment_ids) includes in_progress
        // attempts, so without this guard a started-but-unsubmitted exam
        // shows BOTH the Resume strip and a false "all done" empty-state.
        inProgress.length === 0 && (
          <EmptyState
            icon={ClipboardCheck}
            title="You've sat every available assessment"
            description="Well done. Every published assessment in this programme has an attempt on your record. Your results are on the My progress page."
            cta={{ label: "View my progress", to: "/progress" }}
          />
        )}

      {/* Gate the grid on gridLoading so a student doesn't see a Start card
          for an already-taken assessment in the window before the
          student-dashboard fetch supplies takenAssessmentIds. The skeleton
          above covers that window. */}
      {!gridLoading && (
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {/* visibleAssessments already hides already-taken papers (the API's
            takenAssessmentIds signal) and in-progress ones (the runtime
            signal) so the candidate never sees both a Start card and a
            Resume card for the same exam. */}
        {visibleAssessments.map((a) => {
          const Icon = MODULE_ICON[a.module] ?? BookOpen;
          // Module palette gives the icon chip + module badge their
          // colour. Aligns the student dashboard cards with the
          // authoring / sitting surfaces so the same exam keeps the
          // same colour identity end to end.
          const palette = modulePalette(a.module);
          // With any exam in progress we don't offer "Start" on other
          // cards — the candidate must finish / submit the live one
          // first. We match the API-side guard in startAttempt() so
          // the UI never has a working button that the server will
          // refuse. `hasBlockingAttempt` stays true even for the
          // card's own assessment so the Start CTA is consistent;
          // the assessment-in-progress card itself is filtered out
          // of this grid entirely — the resume strip above covers it.
          const blocked = hasBlockingAttempt;
          // Keep Start disabled until the in-progress check has settled.
          // The blocking-attempt signal arrives from a fetch that
          // resolves AFTER the grid first renders, so without this a fast
          // candidate could click Start into a raw server rejection.
          const notReady = !inProgressLoaded;
          // Scheduling window — same pill the sittings use. Parse the window
          // once and feed the SAME boundaries to both the pill and the
          // disabled-state derivation so the pill label and the Start CTA's
          // disabled state always agree (right on a boundary too). When the
          // paper hasn't opened yet (or has closed) the Start CTA is disabled
          // and relabelled so the candidate isn't offered a button the server
          // would reject; the ticking clock flips "Not yet open" to an enabled
          // Start the moment it opens — no manual reload needed.
          const opensMs = parseScheduleStampMs(a.opens_at);
          const closesMs = parseScheduleStampMs(a.closes_at);
          const pill = dashboardSchedulePill(opensMs, closesMs, nowTick);
          const notYetOpen = opensMs != null && nowTick < opensMs;
          const alreadyClosed = closesMs != null && nowTick >= closesMs;
          const windowBlocked = notYetOpen || alreadyClosed;
          return (
            <Card
              key={a.id}
              className={cn(
                "group relative flex flex-col overflow-hidden border-border/60 shadow-elev-1 transition-all",
                !blocked && "hover:-translate-y-0.5 hover:shadow-elev-2",
                (blocked || windowBlocked) && "opacity-75",
              )}
            >
              <div
                aria-hidden
                className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent opacity-0 transition-opacity group-hover:opacity-100"
              />
              <CardHeader className="space-y-3">
                <div className="flex items-start justify-between gap-2">
                  <span
                    className={cn(
                      "grid h-9 w-9 place-items-center rounded-lg",
                      palette.accentBg,
                      palette.accentText,
                    )}
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                  </span>
                  <div className="flex flex-wrap gap-1.5">
                    <LevelBadge code={a.level_code} />
                    <span
                      className={cn(
                        "inline-flex items-center rounded-md px-2 py-0.5 text-xs font-medium",
                        palette.accentBg,
                        palette.accentText,
                      )}
                    >
                      {MODULE_LABELS[a.module] ?? a.module}
                    </span>
                  </div>
                </div>
                <CardTitle className="leading-snug">{a.title}</CardTitle>
              </CardHeader>
              <CardContent className="flex-1">
                <dl className="grid grid-cols-2 gap-x-3 gap-y-2 text-xs text-muted-foreground">
                  <div className="flex items-center gap-1.5">
                    <Clock className="h-3.5 w-3.5" />
                    <dd>{Math.round(a.duration_seconds / 60)} min</dd>
                  </div>
                  {a.score_reporting === "band" ? (
                    <div className="flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5" />
                      <dd>Band-graded</dd>
                    </div>
                  ) : a.grading_mode === "raw_score" ? (
                    <div className="flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5" />
                      <dd>Raw score</dd>
                    </div>
                  ) : (
                    <div className="flex items-center gap-1.5">
                      <Target className="h-3.5 w-3.5" />
                      <dd>Pass {a.pass_threshold_percent}%</dd>
                    </div>
                  )}
                  {user?.role === "student" && (
                    <div className="col-span-2">
                      {/* The dashboard hides an assessment once the candidate
                          has any non-cancelled attempt on it, so from here it's
                          always a single sitting — "Up to N attempts" overstated
                          what they actually get. Staff aren't sitting the paper,
                          so the single-attempt rule is irrelevant to them. */}
                      One sitting
                    </div>
                  )}
                  {pill && (
                    <div className="col-span-2">
                      <span
                        className={cn(
                          "inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-medium",
                          pill.classes,
                        )}
                      >
                        <CalendarClock className="h-3 w-3" aria-hidden />
                        {pill.label}
                      </span>
                    </div>
                  )}
                </dl>
              </CardContent>
              <CardFooter>
                {user?.role === "student" ? (
                  <Button
                    onClick={() => start(a.id)}
                    disabled={
                      anyStarting || notReady || blocked || windowBlocked
                    }
                    className="w-full"
                    title={
                      notReady
                        ? "Checking your in-progress attempts…"
                        : blocked
                          ? "Finish your in-progress exam before starting a new one"
                          : notYetOpen
                            ? "This assessment hasn't opened yet"
                            : alreadyClosed
                              ? "This assessment has closed"
                              : undefined
                    }
                  >
                    <span>
                      {starting === a.id
                        ? "Starting"
                        : notReady
                          ? "Checking…"
                          : blocked
                            ? "Exam in progress"
                            : notYetOpen
                              ? "Not yet open"
                              : alreadyClosed
                                ? "Closed"
                                : "Start attempt"}
                    </span>
                    {/* Single trailing icon slot — swap loader ↔ arrow
                        on click so the button never shows two icons or
                        shifts width. Blocked state shows no icon — the
                        text alone makes the disabled meaning clear. */}
                    {starting === a.id || notReady ? (
                      <Loader2 className="ml-auto h-4 w-4 animate-spin" />
                    ) : !blocked && !windowBlocked ? (
                      <ArrowRight className="ml-auto h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    ) : null}
                  </Button>
                ) : hasRole(role, "teacher") ? (
                  // Teachers / admins / moderators can't "Start" an
                  // attempt — the server gates /start to students only.
                  // Point them at the authoring preview, which renders
                  // the same paper without creating an attempt row.
                  <Button
                    asChild
                    variant="outline"
                    className="w-full"
                    title="Open as a student would see it (no attempt recorded)"
                  >
                    <Link to={`/authoring/${a.id}/preview`}>
                      Preview
                      <ArrowRight className="ml-auto h-4 w-4 transition-transform group-hover:translate-x-0.5" />
                    </Link>
                  </Button>
                ) : (
                  // Registrar (or any non-teacher staff): the authoring
                  // preview is teacher-gated, so a Preview button would
                  // silently bounce them back to /dashboard. Show a muted
                  // "View only" affordance instead of a dead-end button.
                  <Button
                    variant="outline"
                    className="w-full text-muted-foreground"
                    disabled
                    title="You don't have access to preview this assessment"
                  >
                    View only
                  </Button>
                )}
              </CardFooter>
            </Card>
          );
        })}
      </div>
      )}

      {/* Keyset "Load more" — the grid previously dropped next_cursor and
          silently showed only the first page. When a title/level filter is
          active, loading more rows can surface further matches, so the button
          stays available whenever the server reports more to fetch. */}
      {!gridLoading && assessments && assessments.length > 0 && nextCursor && (
        <div className="flex justify-center">
          <Button
            type="button"
            variant="outline"
            onClick={() => void loadMore()}
            disabled={loadingMore}
          >
            {loadingMore && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            Load more
          </Button>
        </div>
      )}
    </div>
  );
}

const ROLE_LABEL: Record<string, string> = {
  student: "Student",
  teacher: "Teacher",
  moderator: "Moderator",
  tenant_admin: "Organisation admin",
  super_admin: "Super admin",
};

function initialsOf(input: string | undefined): string {
  if (!input) return "EB";
  return input
    .split(/[\s.@]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? "")
    .join("") || "EB";
}

function IdentityHero({
  user,
  orgSlug,
  assessmentsAvailable,
  inProgressCount,
  isExaminer,
  programmeName,
  programmeSlug,
}: {
  user: ReturnType<typeof getUser>;
  orgSlug: string | null;
  assessmentsAvailable: number | null;
  inProgressCount: number;
  isExaminer: boolean;
  programmeName: string | null;
  programmeSlug: string | null;
}) {
  const name = user?.full_name || user?.email || "Signed in";
  const roleLabel = user?.role ? ROLE_LABEL[user.role] ?? user.role : null;
  const initials = initialsOf(user?.full_name || user?.email);
  const hour = new Date().getHours();
  const greeting =
    hour < 5 ? "Working late" : hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";

  return (
    <section className="relative overflow-hidden border border-border bg-card shadow-elev-1">
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-brand-glow" />
      <div aria-hidden className="pointer-events-none absolute inset-0 bg-dot-grid opacity-40" />
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 h-1 bg-brand-gradient"
      />
      <div className="relative grid gap-6 p-6 sm:p-8 lg:grid-cols-[minmax(0,1fr)_auto] lg:items-center">
        <div className="flex min-w-0 items-start gap-4 sm:gap-5">
          <div
            aria-hidden
            className="grid h-14 w-14 shrink-0 place-items-center bg-brand-gradient text-base font-semibold text-primary-foreground shadow-elev-1 sm:h-16 sm:w-16 sm:text-lg"
          >
            {initials}
          </div>
          <div className="min-w-0 space-y-1.5">
            <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-accent-foreground">
              {greeting}
            </p>
            <h1 className="truncate font-serif text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">
              {name}
            </h1>
            <div className="flex flex-wrap items-center gap-1.5">
              {roleLabel && (
                <Badge variant="secondary" className="text-[11px]">
                  {roleLabel}
                </Badge>
              )}
              {isExaminer && (
                <Badge variant="success" className="text-[11px]">
                  Examiner
                </Badge>
              )}
              {programmeName && (
                <Badge
                  variant={programmeSlug === "heda" ? "warning" : "outline"}
                  className="text-[11px]"
                >
                  {/* Render the server's real programme name rather than a
                      hardcoded HEDA/FS-English fork that discarded it and
                      would mislabel any future programme. Short slug-derived
                      fallback only when the name is somehow missing. */}
                  {programmeName ||
                    (programmeSlug === "heda" ? "HEDA" : "FS English")}
                </Badge>
              )}
              {orgSlug && (
                <span className="inline-flex items-center gap-1 text-xs font-medium text-muted-foreground">
                  <span aria-hidden>·</span>
                  <span className="font-mono">{orgSlug}</span>
                </span>
              )}
            </div>
            {user?.email && user.full_name && (
              <p className="truncate text-xs text-muted-foreground">{user.email}</p>
            )}
          </div>
        </div>

        <div className="flex flex-col gap-3 sm:flex-row lg:flex-col lg:items-end">
          <div className="grid grid-cols-2 gap-2 sm:gap-3">
            <HeroStat
              // Staff publish assessments — they don't "take" them, so the
              // student "Available" framing reads wrong. Teachers/admins see
              // "Published"; students keep "Available".
              label={hasRole(user?.role ?? "student", "teacher") ? "Published" : "Available"}
              value={assessmentsAvailable}
              sub="assessments"
            />
            <HeroStat
              label="In progress"
              value={inProgressCount}
              sub={inProgressCount === 1 ? "attempt" : "attempts"}
              tone={inProgressCount > 0 ? "warn" : "muted"}
            />
          </div>
          {isExaminer && (
            <Button asChild variant="outline" size="sm" className="sm:self-end">
              <Link to="/marking">
                Marking queue <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          )}
        </div>
      </div>
    </section>
  );
}

function HeroStat({
  label,
  value,
  sub,
  tone = "muted",
}: {
  label: string;
  value: number | null;
  sub: string;
  tone?: "muted" | "warn";
}) {
  return (
    <div
      className={cn(
        "min-w-[7rem] border border-border bg-background/70 px-3 py-2 backdrop-blur-sm",
        tone === "warn" && "border-warning/40 bg-warning/5",
      )}
    >
      <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
        {label}
      </div>
      <div className="flex items-baseline gap-1">
        <span className="font-serif text-2xl font-semibold tabular-nums">
          {value?.toLocaleString("en-GB") ?? "—"}
        </span>
        <span className="text-[11px] text-muted-foreground">{sub}</span>
      </div>
    </div>
  );
}

function FirstRunChecklist() {
  // Three-step welcome strip shown to tenant admins whose org has no
  // assessments yet. Intentionally declarative (static links) rather than
  // a state machine — dismissal "completeness" is implied by the Dashboard
  // no longer being empty.
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Get started
      </h2>
      <div className="grid gap-3 sm:grid-cols-3">
        <ChecklistTile
          step={1}
          title="Invite your first teacher"
          body="Teachers can create assessments, view stats, and (if examiners) mark attempts."
          cta={{ label: "Open members", to: "/org/members" }}
        />
        <ChecklistTile
          step={2}
          title="Create your first assessment"
          body="Pick a module and draft the first reading/writing/speaking/listening paper."
          cta={{ label: "Open authoring", to: "/authoring" }}
        />
        <ChecklistTile
          step={3}
          title="Check your plan"
          body="Review usage and adjust your subscription before inviting the cohort."
          // The billing surface (plan, usage, Stripe portal) lives in the
          // admin-only BillingPanel on /stats — point here rather than the
          // old dead "/" link.
          cta={{ label: "Billing", to: "/stats" }}
        />
      </div>
    </section>
  );
}

function ChecklistTile({
  step,
  title,
  body,
  cta,
}: {
  step: number;
  title: string;
  body: string;
  cta: { label: string; to: string };
}) {
  return (
    <Card>
      <CardHeader className="space-y-1 pb-2">
        <div className="flex items-center gap-2">
          <span
            aria-hidden
            className="grid h-6 w-6 place-items-center bg-accent text-[11px] font-semibold text-accent-foreground"
          >
            {step}
          </span>
          <CardTitle className="text-sm">{title}</CardTitle>
        </div>
      </CardHeader>
      <CardContent className="space-y-3 pt-0 text-xs text-muted-foreground">
        <p>{body}</p>
        <Button asChild size="sm" variant="outline">
          <Link to={cta.to}>{cta.label}</Link>
        </Button>
      </CardContent>
    </Card>
  );
}

// Placeholder block for a role widget / sittings strip that's still loading.
// Mirrors the section rhythm (a short heading bar + a card) so the layout
// doesn't jump when the real widget swaps in.
function WidgetSkeleton({ rows = 2 }: { rows?: number }) {
  return (
    <section className="space-y-2">
      <Skeleton className="h-4 w-40" />
      <div
        className={cn(
          "grid gap-3",
          rows >= 4 ? "sm:grid-cols-2 lg:grid-cols-4" : "sm:grid-cols-2",
        )}
      >
        {Array.from({ length: rows }).map((_, i) => (
          <Card key={i}>
            <CardContent className="space-y-3 py-4">
              <Skeleton className="h-3 w-24" />
              <Skeleton className="h-7 w-16" />
              <Skeleton className="h-3 w-32" />
            </CardContent>
          </Card>
        ))}
      </div>
    </section>
  );
}

// Quiet inline error for a role widget / sittings strip whose fetch failed
// with a non-403 (a 5xx or network drop). Reuses the same destructive-tinted
// alert + ghost Retry shape as the assessments-grid error so the surface stays
// visually consistent; intentionally low-key (a single row) because a failed
// secondary widget shouldn't dominate the page.
function WidgetError({
  label = "Couldn't load this section.",
  onRetry,
}: {
  label?: string;
  onRetry: () => void;
}) {
  return (
    <div
      role="alert"
      className="flex items-center justify-between gap-3 rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive"
    >
      <span>{label}</span>
      <Button
        variant="ghost"
        size="sm"
        className="h-7 shrink-0 text-destructive hover:text-destructive"
        onClick={onRetry}
      >
        Retry
      </Button>
    </div>
  );
}

// --- Enterprise widgets ---

function QuickActions({
  isExaminer,
  isTenantAdmin,
  isSlipManager,
}: {
  isExaminer: boolean;
  isTenantAdmin: boolean;
  isSlipManager: boolean;
}) {
  // Icon-first buttons for the three or four most-used jumps. Order chosen so
  // the "create something" actions sit leftmost — power users reach for
  // Authoring far more often than they reach for Settings.
  return (
    <div className="flex flex-wrap gap-2">
      <Button asChild size="sm" variant="outline">
        <Link to="/authoring">
          <PencilRuler className="mr-1.5 h-3.5 w-3.5" /> Authoring
        </Link>
      </Button>
      <Button asChild size="sm" variant="outline">
        <Link to="/stats">
          <Activity className="mr-1.5 h-3.5 w-3.5" /> Stats
        </Link>
      </Button>
      {isExaminer && (
        <Button asChild size="sm" variant="outline">
          <Link to="/marking">
            <ClipboardCheck className="mr-1.5 h-3.5 w-3.5" /> Marking queue
          </Link>
        </Button>
      )}
      {isSlipManager && (
        <Button asChild size="sm" variant="outline">
          <Link to="/org/candidate-slips">
            <Ticket className="mr-1.5 h-3.5 w-3.5" /> Entry slips
          </Link>
        </Button>
      )}
      {isTenantAdmin && (
        <>
          <Button asChild size="sm" variant="outline">
            <Link to="/org/members">
              <UserPlus className="mr-1.5 h-3.5 w-3.5" /> Invite member
            </Link>
          </Button>
          <Button asChild size="sm" variant="outline">
            <Link to="/org/marking-workload">
              <Gauge className="mr-1.5 h-3.5 w-3.5" /> Workload
            </Link>
          </Button>
        </>
      )}
    </div>
  );
}

// Prominent Entry-slips banner for slip managers (registrars + admins). The
// dashboard grid is full of assessments a registrar can't act on, so this is
// their primary in-flow jump to the job they actually do. Styled as a brand
// callout (not a quiet outline button) so it reads as the headline action.
function SlipManagerCTA() {
  return (
    <section className="flex flex-col gap-3 border border-primary/30 bg-primary/5 p-4 sm:flex-row sm:items-center sm:justify-between">
      <div className="flex items-start gap-3">
        <span
          aria-hidden
          className="grid h-10 w-10 shrink-0 place-items-center rounded-lg bg-primary/10 text-primary"
        >
          <Ticket className="h-5 w-5" />
        </span>
        <div className="space-y-0.5">
          <h2 className="text-sm font-semibold text-foreground">
            Manage candidate entry slips
          </h2>
          <p className="text-xs text-muted-foreground">
            Issue, reissue, and print the entry slips candidates use to sign in
            to their exams.
          </p>
        </div>
      </div>
      <Button asChild className="shrink-0 sm:self-center">
        <Link to="/org/candidate-slips">
          <Ticket className="mr-2 h-4 w-4" /> Entry slips
          <ArrowRight className="ml-2 h-4 w-4" />
        </Link>
      </Button>
    </section>
  );
}

function OrgMetrics({
  headline,
}: {
  headline: OrgDashboard["headline"];
}) {
  // Five KPIs in a row — mirrors the typical SaaS admin header strip. The
  // "awaiting review" tile flips to warning tone above zero so it pulls the
  // eye when something genuinely needs attention.
  const tiles = [
    {
      label: "Published",
      value: headline.published_assessments,
      sub: "assessments",
      icon: Library,
    },
    {
      label: "Attempts (30d)",
      value: headline.attempts_30d,
      sub: `of ${headline.total_attempts.toLocaleString("en-GB")} total`,
      icon: Activity,
    },
    {
      label: "Awaiting review",
      value: headline.awaiting_review,
      sub: headline.awaiting_review === 1 ? "attempt" : "attempts",
      icon: ClipboardCheck,
      tone: headline.awaiting_review > 0 ? ("warn" as const) : ("muted" as const),
    },
    {
      label: "Active students (30d)",
      value: headline.active_students_30d,
      sub: "signed in",
      icon: Users,
    },
  ];
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Organisation at a glance
      </h2>
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        {tiles.map((t) => (
          <MetricTile key={t.label} {...t} />
        ))}
      </div>
    </section>
  );
}

function MetricTile({
  label,
  value,
  sub,
  icon: Icon,
  tone = "muted",
}: {
  label: string;
  value: number;
  sub: string;
  icon: typeof Activity;
  tone?: "muted" | "warn";
}) {
  return (
    <div
      className={cn(
        "border border-border bg-card p-4",
        tone === "warn" && "border-warning/40 bg-warning/5",
      )}
    >
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
          {label}
        </span>
        <Icon
          className={cn(
            "h-4 w-4 text-muted-foreground",
            tone === "warn" && "text-warning-text",
          )}
          aria-hidden
        />
      </div>
      <div className="mt-1 flex items-baseline gap-1.5">
        <span className="font-serif text-3xl font-semibold tabular-nums">
          {value.toLocaleString("en-GB")}
        </span>
        <span className="text-xs text-muted-foreground">{sub}</span>
      </div>
    </div>
  );
}

function PlanUsageWidget({ usage }: { usage: OrgUsage }) {
  // Published vs cap + attempts vs cap. Each row shows a proportional bar +
  // alerts when usage crosses 80%. Unlimited caps (-1) render as a quiet
  // "Unlimited" label so the visual vocabulary stays consistent.
  const rows = [
    {
      label: "Published assessments",
      current: usage.caps.publish.current,
      limit: usage.caps.publish.limit,
    },
    {
      label: "Attempts this month",
      current: usage.caps.attempts.current,
      limit: usage.caps.attempts.limit,
    },
  ];
  return (
    <section className="space-y-2">
      <div className="flex items-center justify-between">
        <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
          Plan usage
        </h2>
        <Badge variant="outline" className="text-[10px] font-medium uppercase">
          {usage.plan.display_name}
        </Badge>
      </div>
      <Card>
        <CardContent className="space-y-4 py-4">
          {rows.map((row) => (
            <UsageRow key={row.label} {...row} />
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

function UsageRow({
  label,
  current,
  limit,
}: {
  label: string;
  current: number;
  limit: number;
}) {
  const unlimited = limit < 0;
  const pct = unlimited ? 0 : Math.min(100, Math.round((current / Math.max(1, limit)) * 100));
  const tone = unlimited
    ? "muted"
    : pct >= 95
      ? "critical"
      : pct >= 80
        ? "warn"
        : "ok";
  return (
    <div className="space-y-1">
      <div className="flex items-baseline justify-between gap-3 text-sm">
        <span className="font-medium">{label}</span>
        <span className="tabular-nums text-muted-foreground">
          {current.toLocaleString("en-GB")} /{" "}
          {unlimited ? "∞" : limit.toLocaleString("en-GB")}
        </span>
      </div>
      {!unlimited && (
        <Progress
          value={Math.max(2, pct)}
          className={cn(
            tone === "warn" && "[&>*]:bg-warning",
            tone === "critical" && "[&>*]:bg-destructive",
          )}
          aria-label={label}
        />
      )}
      {unlimited && (
        <p className="text-xs text-muted-foreground">Unlimited on this plan.</p>
      )}
      {tone === "warn" && (
        <p className="text-xs text-warning-text">Approaching cap. Consider upgrading.</p>
      )}
      {tone === "critical" && (
        <p className="text-xs text-destructive">At cap. New actions will be refused.</p>
      )}
    </div>
  );
}

function TeacherInsights({
  totals,
  byAssessment,
  isExaminer,
}: {
  totals: TeacherDashboard["totals"];
  byAssessment: TeacherDashboard["by_assessment"];
  isExaminer: boolean;
}) {
  const awaiting = totals.awaiting_review;
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Your teaching activity
      </h2>
      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_minmax(0,1.6fr)]">
        {awaiting > 0 && isExaminer ? (
          <Card className="border-warning/40 bg-warning/5">
            <CardContent className="space-y-2 py-4">
              <div className="flex items-center gap-2">
                <ClipboardCheck className="h-4 w-4 text-warning-text" aria-hidden />
                <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-warning-text">
                  Attention
                </span>
              </div>
              <p className="font-serif text-3xl font-semibold tabular-nums">
                {awaiting}
              </p>
              <p className="text-sm text-muted-foreground">
                {awaiting === 1 ? "attempt" : "attempts"} awaiting your review.
              </p>
              <Button asChild size="sm" className="mt-2">
                <Link to="/marking">
                  Open marking queue <ArrowRight className="ml-1.5 h-3.5 w-3.5" />
                </Link>
              </Button>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="space-y-2 py-4">
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Last 30 days
              </span>
              <p className="font-serif text-3xl font-semibold tabular-nums">
                {totals.attempts_30d}
              </p>
              <p className="text-sm text-muted-foreground">
                attempts across your published assessments.
              </p>
            </CardContent>
          </Card>
        )}
        <Card>
          <CardContent className="space-y-2 py-4">
            <div className="flex items-center justify-between">
              <span className="text-[11px] font-semibold uppercase tracking-[0.1em] text-muted-foreground">
                Top assessments
              </span>
              <Button asChild size="sm" variant="link" className="h-auto px-0">
                <Link to="/stats">
                  View stats <ArrowRight className="ml-1 h-3 w-3" />
                </Link>
              </Button>
            </div>
            {byAssessment.length === 0 ? (
              <p className="text-sm text-muted-foreground">
                No submissions yet.
              </p>
            ) : (
              <ul className="divide-y">
                {byAssessment.map((a) => (
                  <li key={a.assessment_id} className="flex items-baseline justify-between gap-3 py-2 text-sm">
                    <Link
                      to={`/authoring/${a.assessment_id}`}
                      className="truncate font-medium underline-offset-4 hover:underline"
                    >
                      {a.title}
                    </Link>
                    <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground tabular-nums">
                      {a.attempts_count} attempt{a.attempts_count === 1 ? "" : "s"}
                      {a.avg_score_percent != null
                        ? ` · ${a.avg_score_percent}% avg`
                        : ""}
                    </span>
                  </li>
                ))}
              </ul>
            )}
          </CardContent>
        </Card>
      </div>
    </section>
  );
}

function RecentAuditFeed({
  events,
}: {
  events: OrgDashboard["recent_audit"];
}) {
  return (
    <section className="space-y-2">
      <h2 className="text-sm font-semibold uppercase tracking-[0.08em] text-muted-foreground">
        Recent activity
      </h2>
      <Card>
        <CardContent className="divide-y p-0">
          {events.map((e) => (
            <div key={e.id} className="flex items-start justify-between gap-3 p-3 text-sm">
              <div className="min-w-0 space-y-0.5">
                <p className="truncate font-mono text-xs">{e.action}</p>
                <p className="text-xs text-muted-foreground">
                  {e.target_type && (
                    <>
                      <span className="capitalize">{e.target_type}</span>
                      {e.target_id ? ` · ${e.target_id.slice(0, 8)}` : ""}
                      {" · "}
                    </>
                  )}
                  {e.actor_name ? (
                    <span title={e.actor_email ?? undefined}>by {e.actor_name}</span>
                  ) : e.actor_user_id ? (
                    `by ${e.actor_user_id.slice(0, 8)}`
                  ) : (
                    "system"
                  )}
                </p>
              </div>
              <span className="shrink-0 whitespace-nowrap text-xs text-muted-foreground">
                {formatDateTime(e.created_at)}
              </span>
            </div>
          ))}
        </CardContent>
      </Card>
    </section>
  );
}

// Single-select module filter rendered as a proper ARIA radiogroup so
// screen readers announce it as a single-choice control (not a set of
// independent toggles) and keyboard users can move the selection with
// the arrow keys. Visual styling reuses the outline Toggle variant so it
// matches the rest of the surface. Roving tabindex: only the selected
// radio is tabbable; ←/→/↑/↓ move and select the next/previous option.
// Level chip group — same accessible radiogroup pattern as ModuleFilterGroup
// (arrow-key roving focus, exactly one tabbable radio), but narrowing the
// loaded catalogue by level code client-side. "all" leads so the default
// (no level filter) stays one keystroke away.
function LevelFilterGroup({
  levels,
  value,
  onChange,
}: {
  levels: string[];
  value: string;
  onChange: (level: string) => void;
}) {
  const refs = useRef<Array<HTMLButtonElement | null>>([]);

  function focusAndSelect(index: number) {
    const next = (index + levels.length) % levels.length;
    const target = levels[next];
    if (target == null) return;
    onChange(target);
    refs.current[next]?.focus();
  }

  function onKeyDown(e: React.KeyboardEvent<HTMLButtonElement>, index: number) {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowDown":
        e.preventDefault();
        focusAndSelect(index + 1);
        break;
      case "ArrowLeft":
      case "ArrowUp":
        e.preventDefault();
        focusAndSelect(index - 1);
        break;
      case "Home":
        e.preventDefault();
        focusAndSelect(0);
        break;
      case "End":
        e.preventDefault();
        focusAndSelect(levels.length - 1);
        break;
      default:
        break;
    }
  }

  const selectedIndex = levels.indexOf(value);
  const tabbableIndex = selectedIndex >= 0 ? selectedIndex : 0;

  return (
    <div
      role="radiogroup"
      aria-label="Filter assessments by level"
      className="flex flex-wrap gap-2"
    >
      {levels.map((lvl, i) => {
        const checked = value === lvl;
        return (
          <button
            key={lvl}
            ref={(el) => {
              refs.current[i] = el;
            }}
            type="button"
            role="radio"
            aria-checked={checked}
            tabIndex={i === tabbableIndex ? 0 : -1}
            data-state={checked ? "on" : "off"}
            onClick={() => onChange(lvl)}
            onKeyDown={(e) => onKeyDown(e, i)}
            className={cn(toggleVariants({ variant: "outline", size: "sm" }))}
          >
            {lvl === "all" ? "All levels" : lvl}
          </button>
        );
      })}
    </div>
  );
}

// Schedule status pill — mirror of SittingsList.schedulePill, kept
// inline here rather than exported to avoid bloating the shared
// surface (helper is trivial). When the sitting has no opens/closes
// at all, returns null and the "always available" line stays.
//
// Takes already-parsed millisecond boundaries (not raw strings) so the
// pill and the card's `windowBlocked` disabled state are derived from the
// SAME numbers — a second internal parse could otherwise let the label and
// the button-disabled state momentarily disagree right at a boundary.
function dashboardSchedulePill(
  opens: number | null,
  closes: number | null,
  // Caller passes the ticking `nowTick` so the pill recomputes on each tick
  // rather than freezing at first render. Defaults to Date.now() for any
  // caller that doesn't thread the clock.
  now: number = Date.now(),
  // When set, a still-open window whose close falls within this many ms
  // escalates its "Closes …" pill to a destructive tone. Omitted by the
  // catalogue grid + sittings strip (calm as before); the resume strip passes
  // it so a candidate running out the clock gets an urgent cue. The open/close
  // boundaries and label are untouched — only the tone changes.
  urgentBeforeMs?: number,
): { label: string; classes: string } | null {
  if (opens == null && closes == null) return null;
  if (closes != null && now >= closes) {
    return {
      label: "Closed",
      classes:
        "border-muted-foreground/30 bg-muted text-muted-foreground",
    };
  }
  if (opens != null && now < opens) {
    return {
      label: `Opens ${relativeFuture(opens - now)}`,
      classes:
        "border-amber-500/40 bg-amber-50 text-amber-900 dark:bg-amber-950/40 dark:text-amber-100",
    };
  }
  if (closes != null) {
    const urgent =
      urgentBeforeMs != null && closes - now <= urgentBeforeMs;
    return {
      label: `Closes ${relativeFuture(closes - now)}`,
      classes: urgent
        ? "border-destructive/40 bg-destructive/10 text-destructive"
        : "border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100",
    };
  }
  return {
    label: "Live now",
    classes:
      "border-emerald-500/40 bg-emerald-50 text-emerald-900 dark:bg-emerald-950/40 dark:text-emerald-100",
  };
}

function relativeFuture(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const h = Math.floor(m / 60);
  const d = Math.floor(h / 24);
  if (d >= 1) return `in ${d}d`;
  if (h >= 1) return `in ${h}h`;
  if (m >= 1) return `in ${m}m`;
  return "soon";
}

function useIsExaminer(): boolean {
  // Read the examiner flag straight from the cached user (localStorage) —
  // exactly as ExaminerRoute, Sidebar, and the CommandPalette already do —
  // rather than paying for a Dashboard-mount api.me() round-trip. The stored
  // is_examiner/role are kept authoritative by the always-mounted UserMenu,
  // which reconciles them against the server on each app boot and org switch.
  // The rule mirrors the API's requireExaminer: is_examiner OR role moderator
  // (tenant admins don't auto-qualify; students/registrars never do).
  const user = getUser();
  const role = user?.role ?? "student";
  return !!user?.is_examiner || role === "moderator";
}
